import { useEffect, useState, useSyncExternalStore } from "react";
import { ChevronDown, ChevronUp, Columns2, Layers, Rows3 } from "lucide-react";

import { useAuth } from "@/components/AuthProvider";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { WORKSPACE_AVATAR_SYNC_OPERATOR_EMAIL } from "@/lib/workspaceAvatarSync";
import {
  getHubBackendDegradedState,
  subscribeHubBackendDegraded,
} from "@/lib/hubBackendDegraded";
import {
  getHubDbPoolHolderStreamState,
  subscribeHubDbPoolHolderStream,
} from "@/lib/hubDbPoolHolderStream";
import {
  POOL_ADMIN_OVERLAY_OCCUPANCY_HOVER_HINT,
  POOL_HOLDER_STREAM_DISCONNECTED_TEXT,
  POOL_HOLDER_STREAM_RECONNECTING_TEXT,
  POOL_LISTEN_OWN_CONNECTION_HELD_TEXT,
  POOL_LISTEN_OWN_CONNECTION_IDLE_TEXT,
  EMPTY_HUB_DB_POOL_HOLDER_SNAPSHOT,
  THIS_TAB_QUEUE_ID,
  hubProcessQueueCopy,
  overlayProcessQueues,
  poolAdminOverlaySummary,
  poolHolderBannerRowsFromHolders,
  processQueueShowsHolderSection,
  processWaiterBannerRowsFromWaiters,
  queueOccupancyCaption,
  toggleHubQueueHidden,
  visibleHubProcessQueues,
  type HubDbPoolHolderSnapshot,
  type HubProcessQueueSnapshot,
} from "@/lib/slowRequestBanner";

const HIDDEN_QUEUES_STORAGE_KEY = "hub-admin-overlay-hidden-queues";
const LAYOUT_STORAGE_KEY = "hub-admin-overlay-layout";

type OverlayLayout = "stack" | "columns";

function canSeeHubDbPoolOverlay(email: string | null | undefined): boolean {
  return (email?.trim().toLowerCase() ?? "") === WORKSPACE_AVATAR_SYNC_OPERATOR_EMAIL;
}

function readHiddenQueueIds(): string[] {
  try {
    const raw = window.sessionStorage.getItem(HIDDEN_QUEUES_STORAGE_KEY);
    if (raw == null || raw.trim() === "") {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((value): value is string => typeof value === "string");
  } catch {
    return [];
  }
}

function readOverlayLayout(): OverlayLayout {
  try {
    return window.sessionStorage.getItem(LAYOUT_STORAGE_KEY) === "stack" ? "stack" : "columns";
  } catch {
    return "columns";
  }
}

function QueueJobList(props: {
  rows: Array<{
    id: number;
    title: string;
    pathDisplay: string | null;
    userLabel: string | null;
    elapsedSeconds: number;
  }>;
  emptyText: string;
  keyPrefix: string;
  wrapPaths?: boolean;
}): JSX.Element {
  if (props.rows.length === 0) {
    return <p className="mt-2 text-xs text-slate-600">{props.emptyText}</p>;
  }
  const titleClass = props.wrapPaths === true ? "break-words text-sm font-medium text-slate-900" : "truncate text-sm font-medium text-slate-900";
  const pathClass =
    props.wrapPaths === true
      ? "break-all font-mono text-[11px] text-slate-600"
      : "truncate font-mono text-[11px] text-slate-600";
  return (
    <ul className="mt-2 space-y-1.5">
      {props.rows.map((row) => (
        <li key={`${props.keyPrefix}-${row.id}`} className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className={titleClass}>{row.title}</p>
              {row.pathDisplay != null ? <p className={pathClass}>{row.pathDisplay}</p> : null}
              {row.userLabel != null ? (
                <p className="truncate text-xs text-slate-500">{row.userLabel}</p>
              ) : null}
            </div>
            <span className="shrink-0 tabular-nums text-xs text-slate-500">{row.elapsedSeconds}s</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function processQueueHolderHoverHint(
  queue: HubProcessQueueSnapshot,
  snapshot: HubDbPoolHolderSnapshot | null,
  listenHeld?: boolean,
): string {
  const copy = hubProcessQueueCopy(queue.id);
  const occupancy = snapshot != null ? queueOccupancyCaption(queue, snapshot) : copy.chipLabel.toLowerCase();
  const parts = [`${occupancy}.`, POOL_ADMIN_OVERLAY_OCCUPANCY_HOVER_HINT, copy.holderHelp];
  if (queue.id === "supabase-query") {
    parts.push(listenHeld ? POOL_LISTEN_OWN_CONNECTION_HELD_TEXT : POOL_LISTEN_OWN_CONNECTION_IDLE_TEXT);
  }
  return parts.join(" ");
}

function QueueSectionTitle(props: { title: string; hoverHint: string }): JSX.Element {
  return (
    <div className="flex items-center gap-1">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-700">{props.title}</h2>
      <HoverHintWithGlyph hoverHint={props.hoverHint} />
    </div>
  );
}

function ProcessQueueSections(props: {
  queue: HubProcessQueueSnapshot;
  snapshot: HubDbPoolHolderSnapshot | null;
  nowMs: number;
  statusText: string | null;
  listenHeld?: boolean;
}): JSX.Element {
  const copy = hubProcessQueueCopy(props.queue.id);
  const holderRows = poolHolderBannerRowsFromHolders(props.queue.holders, props.nowMs);
  const waiterRows = processWaiterBannerRowsFromWaiters(props.queue.waiters, props.nowMs);
  const holderHoverHint = processQueueHolderHoverHint(props.queue, props.snapshot, props.listenHeld);
  const showHolders = processQueueShowsHolderSection(props.queue);
  const waiterHoverHint = `${queueOccupancyCaption(props.queue, props.snapshot ?? EMPTY_HUB_DB_POOL_HOLDER_SNAPSHOT)}. ${POOL_ADMIN_OVERLAY_OCCUPANCY_HOVER_HINT} ${copy.waiterHelp}`;
  return (
    <div className="min-w-0">
      {showHolders ? (
        <section>
          <QueueSectionTitle title={copy.holderTitle} hoverHint={holderHoverHint} />
          {props.statusText != null ? <p className="mt-2 text-xs text-amber-800">{props.statusText}</p> : null}
          {props.queue.seats != null && props.queue.seats.length > 0 ? (
            <ul className="mt-2 space-y-1.5">
              {props.queue.seats.map((seat, seatIndex) => {
                const occupantRows =
                  seat.occupant != null ? poolHolderBannerRowsFromHolders([seat.occupant], props.nowMs) : [];
                const occupant = occupantRows[0];
                return (
                  <li
                    key={`${props.queue.id}-seat-${seat.kind}-${seatIndex}`}
                    className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{seat.label}</p>
                    <div className="mt-1 flex min-h-[3.375rem] items-start justify-between gap-2">
                      {occupant != null ? (
                        <>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-slate-900">{occupant.title}</p>
                            {occupant.pathDisplay != null ? (
                              <p className="truncate font-mono text-[11px] text-slate-600">{occupant.pathDisplay}</p>
                            ) : (
                              <p className="truncate font-mono text-[11px] text-transparent select-none" aria-hidden>
                                -
                              </p>
                            )}
                            {occupant.userLabel != null ? (
                              <p className="truncate text-xs text-slate-500">{occupant.userLabel}</p>
                            ) : (
                              <p className="truncate text-xs text-transparent select-none" aria-hidden>
                                -
                              </p>
                            )}
                          </div>
                          <span className="shrink-0 tabular-nums text-xs text-slate-500">{occupant.elapsedSeconds}s</span>
                        </>
                      ) : (
                        <p className="text-xs text-slate-500">Empty</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : holderRows.length === 0 ? (
            props.statusText == null ? (
              <p className="mt-2 text-xs text-slate-600">{copy.holderEmpty}</p>
            ) : null
          ) : (
            <QueueJobList rows={holderRows} emptyText={copy.holderEmpty} keyPrefix={`${props.queue.id}-holder`} />
          )}
        </section>
      ) : props.statusText != null ? (
        <p className="text-xs text-amber-800">{props.statusText}</p>
      ) : null}
      <section className={showHolders ? "mt-4" : undefined}>
        <QueueSectionTitle title={copy.waiterTitle} hoverHint={waiterHoverHint} />
        <QueueJobList
          rows={waiterRows}
          emptyText={copy.waiterEmpty}
          keyPrefix={`${props.queue.id}-waiter`}
          wrapPaths={props.queue.id === THIS_TAB_QUEUE_ID}
        />
      </section>
    </div>
  );
}

/**
 * Operator-only (e.edwards@smartgridsolutions.com): always-visible process queue
 * chip (bottom right). Expand to toggle queues or view them side by side.
 */
export function HubDbPoolAdminOverlay(): JSX.Element | null {
  const { session } = useAuth();
  const allowed = canSeeHubDbPoolOverlay(session?.user?.email);
  const holderStream = useSyncExternalStore(
    subscribeHubDbPoolHolderStream,
    getHubDbPoolHolderStreamState,
  );
  const degraded = useSyncExternalStore(subscribeHubBackendDegraded, getHubBackendDegradedState);
  const [expanded, setExpanded] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [hiddenQueueIds, setHiddenQueueIds] = useState<string[]>([]);
  const [layout, setLayout] = useState<OverlayLayout>("columns");

  useEffect(() => {
    if (!allowed) {
      return;
    }
    setHiddenQueueIds(readHiddenQueueIds());
    setLayout(readOverlayLayout());
  }, [allowed]);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    setNowMs(Date.now());
    const timer = window.setInterval(() => setNowMs(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [allowed, expanded]);

  if (!allowed) {
    return null;
  }

  const snapshot = holderStream.snapshot;
  const summary = poolAdminOverlaySummary(snapshot, degraded.slowRequests, nowMs);
  const queues = overlayProcessQueues(snapshot, degraded.slowRequests, nowMs);
  const visibleQueues = visibleHubProcessQueues(queues, hiddenQueueIds);
  const queuedCount = queues.reduce((sum, queue) => {
    if (queue.id === THIS_TAB_QUEUE_ID) {
      return sum + queue.waiters.length;
    }
    const extra = queue.id === "supabase-query" ? snapshot?.pool?.waitingCount ?? 0 : 0;
    return sum + Math.max(queue.waiters.length, extra);
  }, 0);
  const busy = queuedCount > 0 || queues.some((queue) => queue.inUse > 0 && queue.max != null && queue.inUse >= queue.max);
  const useColumns = layout === "columns" && visibleQueues.length > 1;
  const columnCount = useColumns ? Math.min(visibleQueues.length, 3) : 1;
  const panelWidth =
    columnCount === 3
      ? "w-[min(100vw-1.5rem,84rem)]"
      : columnCount === 2
        ? "w-[min(100vw-1.5rem,56rem)]"
        : "w-[min(100vw-1.5rem,28rem)]";
  const outerMaxWidth =
    columnCount === 3
      ? "max-w-[min(100vw-1.5rem,84rem)]"
      : columnCount === 2
        ? "max-w-[min(100vw-1.5rem,56rem)]"
        : "max-w-[min(100vw-1.5rem,28rem)]";
  const columnsClass =
    columnCount === 3 ? "grid grid-cols-3 gap-4" : columnCount === 2 ? "grid grid-cols-2 gap-4" : "flex flex-col gap-4";

  let holderStatusText: string | null = null;
  if (holderStream.status === "idle" || holderStream.status === "connecting") {
    holderStatusText = POOL_HOLDER_STREAM_DISCONNECTED_TEXT;
  } else if (holderStream.status === "disconnected") {
    holderStatusText = POOL_HOLDER_STREAM_RECONNECTING_TEXT;
  }

  function persistHidden(next: string[]): void {
    setHiddenQueueIds(next);
    try {
      window.sessionStorage.setItem(HIDDEN_QUEUES_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Ignore quota / private-mode failures.
    }
  }

  function persistLayout(next: OverlayLayout): void {
    setLayout(next);
    try {
      window.sessionStorage.setItem(LAYOUT_STORAGE_KEY, next);
    } catch {
      // Ignore quota / private-mode failures.
    }
  }

  return (
    <div className={`pointer-events-none fixed bottom-3 right-3 z-[80] flex ${outerMaxWidth} flex-col items-end gap-2`}>
      {expanded ? (
        <div
          className={`pointer-events-auto max-h-[min(70vh,36rem)] ${panelWidth} overflow-auto rounded-lg border border-slate-300 bg-white/95 p-3 shadow-lg backdrop-blur-sm`}
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-2">
            <div className="flex flex-wrap gap-1" role="group" aria-label="Queues to show">
              {queues.map((queue) => {
                const copy = hubProcessQueueCopy(queue.id);
                const pressed = visibleQueues.some((visible) => visible.id === queue.id);
                return (
                  <button
                    key={queue.id}
                    type="button"
                    aria-pressed={pressed}
                    className={[
                      "rounded-full border px-2 py-0.5 text-xs font-medium",
                      pressed
                        ? "border-slate-700 bg-slate-800 text-white"
                        : "border-slate-300 bg-white text-slate-600",
                    ].join(" ")}
                    onClick={() => persistHidden(toggleHubQueueHidden(hiddenQueueIds, queue.id))}
                  >
                    {copy.chipLabel}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-1" role="group" aria-label="Queue layout">
              <button
                type="button"
                aria-pressed={layout === "columns"}
                className={[
                  "inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs",
                  layout === "columns"
                    ? "border-slate-700 bg-slate-800 text-white"
                    : "border-slate-300 bg-white text-slate-600",
                ].join(" ")}
                onClick={() => persistLayout("columns")}
              >
                <Columns2 className="h-3 w-3" aria-hidden />
                Side by side
              </button>
              <button
                type="button"
                aria-pressed={layout === "stack"}
                className={[
                  "inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs",
                  layout === "stack"
                    ? "border-slate-700 bg-slate-800 text-white"
                    : "border-slate-300 bg-white text-slate-600",
                ].join(" ")}
                onClick={() => persistLayout("stack")}
              >
                <Rows3 className="h-3 w-3" aria-hidden />
                Stacked
              </button>
            </div>
          </div>
          {queues.length === 0 ? (
            <p className="text-xs text-slate-600">{holderStatusText ?? "No queue snapshot yet."}</p>
          ) : (
            <div className={columnsClass}>
              {visibleQueues.map((queue, index) => (
                <ProcessQueueSections
                  key={queue.id}
                  queue={queue}
                  snapshot={snapshot}
                  nowMs={nowMs}
                  statusText={index === 0 ? holderStatusText : null}
                  listenHeld={snapshot?.listenHeld === true}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}

      <div
        className={[
          "pointer-events-auto inline-flex items-center gap-1 rounded-full border px-2 py-1.5 shadow-md",
          busy
            ? "border-amber-400 bg-amber-50 text-amber-950"
            : "border-slate-300 bg-white/95 text-slate-800",
        ].join(" ")}
      >
        <button
          type="button"
          className="inline-flex items-center gap-2 px-1 text-sm"
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse process queues. ${summary}` : `Expand process queues. ${summary}`}
          onClick={() => setExpanded((current) => !current)}
        >
          <Layers className="h-4 w-4 shrink-0" aria-hidden />
          <span className="max-w-[28rem] truncate font-medium tabular-nums">{summary}</span>
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
          )}
        </button>
      </div>
    </div>
  );
}
